import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Sprint 100 — typed wrapper for the super-admin manual-reconciliation
 * callable. The daily cron writes reports automatically; this is for
 * "I want today's partial-day report before midnight" use cases.
 */

export const ReconcilePaymentsManualInputSchema = z.object({
  brandId: z.string().min(1),
  periodStartMs: z.number().int().positive().optional(),
  periodEndMs: z.number().int().positive().optional(),
});
export type ReconcilePaymentsManualInput = z.infer<typeof ReconcilePaymentsManualInputSchema>;

export const ReconcilePaymentsManualOutputSchema = z.object({
  reportId: z.string(),
  clean: z.boolean(),
  paidIntentsCount: z.number().int().nonnegative(),
  mismatchesCount: z.number().int().nonnegative(),
  totalAmountSen: z.number().int().nonnegative(),
  totalCreditedSen: z.number().int().nonnegative(),
});
export type ReconcilePaymentsManualOutput = z.infer<typeof ReconcilePaymentsManualOutputSchema>;

const reconcileFn: HttpsCallable<ReconcilePaymentsManualInput, ReconcilePaymentsManualOutput> =
  httpsCallable(functions, 'reconcilePaymentsManual');

export async function callReconcilePaymentsManual(
  input: ReconcilePaymentsManualInput,
): Promise<ReconcilePaymentsManualOutput> {
  const parsed = ReconcilePaymentsManualInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('reconcilePaymentsManual.input', parsed.error.flatten());
  }
  const result = await reconcileFn(parsed.data);
  const out = ReconcilePaymentsManualOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('reconcilePaymentsManual.output', out.error.flatten());
  }
  return out.data;
}

// ── Sprint 120 — settlement CSV upload ─────────────────────────────────

export const UploadSettlementCsvInputSchema = z.object({
  brandId: z.string().min(1),
  source: z.enum(['toyyibpay']).default('toyyibpay'),
  csvText: z
    .string()
    .min(1)
    .max(5 * 1024 * 1024),
  fileName: z.string().min(1).max(255),
  intentLookbackDays: z.number().int().min(1).max(180).optional(),
});
export type UploadSettlementCsvInput = z.infer<typeof UploadSettlementCsvInputSchema>;

export const UploadSettlementCsvOutputSchema = z.object({
  reportId: z.string(),
  clean: z.boolean(),
  rowsTotal: z.number().int().nonnegative(),
  rowsParsed: z.number().int().nonnegative(),
  rowsMatched: z.number().int().nonnegative(),
  intentsMissingFromCsv: z.number().int().nonnegative(),
  mismatchesCount: z.number().int().nonnegative(),
  totalCsvAmountSen: z.number().int().nonnegative(),
  totalMatchedIntentSen: z.number().int().nonnegative(),
});
export type UploadSettlementCsvOutput = z.infer<typeof UploadSettlementCsvOutputSchema>;

const uploadSettlementFn: HttpsCallable<UploadSettlementCsvInput, UploadSettlementCsvOutput> =
  httpsCallable(functions, 'uploadSettlementCsv');

export async function callUploadSettlementCsv(
  input: UploadSettlementCsvInput,
): Promise<UploadSettlementCsvOutput> {
  const parsed = UploadSettlementCsvInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('uploadSettlementCsv.input', parsed.error.flatten());
  }
  const result = await uploadSettlementFn(parsed.data);
  const out = UploadSettlementCsvOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('uploadSettlementCsv.output', out.error.flatten());
  }
  return out.data;
}
